import { isPointInRegion, type Region, type Vec2 } from '../utils/geometry';
import { SurfaceType } from '../terrain/SurfaceType';
import type { PinPosition, TeeBox } from './types';

export interface HoleDefinition {
  number: number;
  par: number;
  /** Yardage from the primary (first) tee box. */
  yardage: number;
  teeBoxes: TeeBox[];
  pins: PinPosition[];
  activePinIndex: number;
  fairway: Region;
  rough: Region[];
  green: Region;
  bunkers: Region[];
  /** Ids referencing WaterFeature instances owned by Course (a hazard can border multiple holes). */
  waterHazardIds: string[];
  outOfBounds: Region[];
  /** Ids referencing VegetationField instances associated with this hole, for future organization/culling. */
  vegetationIds: string[];
}

/**
 * Hole owns its data and knows how to classify a point on itself — that
 * classification method is the thing Course wires into Terrain's generic
 * surfaceTypeSampler. Hole never touches Three.js scene graph directly;
 * it's pure data plus geometry queries.
 */
export class Hole {
  constructor(readonly def: HoleDefinition) {}

  get number(): number {
    return this.def.number;
  }

  get par(): number {
    return this.def.par;
  }

  get yardage(): number {
    return this.def.yardage;
  }

  get activePin(): PinPosition {
    return this.def.pins[this.def.activePinIndex] ?? this.def.pins[0];
  }

  get primaryTee(): TeeBox {
    return this.def.teeBoxes[0];
  }

  /**
   * Classifies a world-space point against this hole's own region data.
   * Bunkers/green checked before fairway (they can sit inside/near the
   * fairway boundary); out-of-bounds and rough are checked last.
   */
  surfaceTypeAt(point: Vec2): SurfaceType {
    if (this.def.bunkers.some((region) => isPointInRegion(point, region)))
      return SurfaceType.Bunker;
    if (isPointInRegion(point, this.def.green)) return SurfaceType.Green;
    if (isPointInRegion(point, this.def.fairway)) return SurfaceType.Fairway;
    if (this.def.outOfBounds.some((region) => isPointInRegion(point, region)))
      return SurfaceType.OutOfBounds;
    if (this.def.rough.some((region) => isPointInRegion(point, region))) return SurfaceType.Rough;
    return SurfaceType.Rough;
  }
}
